'use client';

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/shad';
import { toggleVariants } from '@/comps/primitives/toggle';
import { useMemo } from 'react';

type ToggleGroupLayout = 'separated' | 'grouped';

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants> & { layout: ToggleGroupLayout }>({
  size: 'default',
  variant: 'default',
  layout: 'separated',
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants> & { layout?: ToggleGroupLayout }
>(({ className, variant, size, layout = 'separated', children, ...props }, ref) => {
  const value = useMemo(() => ({ variant, size, layout }), [size, variant, layout]);
  return (
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn('flex items-center justify-center', layout === 'separated' && 'gap-1', className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={value}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        'disabled:pointer-events-auto disabled:cursor-not-allowed',
        context.layout === 'grouped' && ['rounded-none', '-ml-px', 'first:rounded-l-md last:rounded-r-md'],
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
